/**
 * lib/sync/syncWorker.ts
 * Sincronización Dexie ↔ Supabase — PAGINADA, RESUMIBLE, IDENTIFICADA POR ALCANCE
 * e INCREMENTAL (ESCALA 0A/0B).
 *
 * ESCALA 0B:
 *   • Cada corrida se identifica por un SCOPE seguro (userId + business + store),
 *     no por `entity:_:_` → ningún cursor se comparte entre usuarios/empresas.
 *   • La frescura (sync_meta) SOLO se actualiza si la corrida COMPLETÓ (nunca en
 *     interrupted/failed).
 *   • Modo 'incremental' (default) arranca desde el completed checkpoint;
 *     'full_reconciliation' arranca desde cero con snapshot propio.
 *
 * NOTA: sync_meta.last_sync_at es solo un indicador local de "hace cuánto se
 * refrescó" para la UI de staleness; NUNCA es la frontera de sincronización. La
 * autoridad del delta es el completed checkpoint server-side (db.sync_runs).
 */

import type { PostgrestError } from '@supabase/supabase-js'
import type { Table } from 'dexie'
import { db } from '@lib/dexie/schema'
import type {
  LocalProduct,
  LocalClient,
  LocalCategory,
  LocalStock,
  LocalBusiness,
  LocalStore,
  LocalUserBusinessAccess,
  LocalUserStoreAccess,
  SyncRunMode,
} from '@lib/dexie/schema'
import { supabase } from '@lib/supabase/client'
import {
  runPaginatedSync,
  keysetOrFilter,
  scopeFingerprint,
  type KeysetCursor,
  type SyncScope,
} from './paginate'
import { withScopeLock } from './syncLock'
import { createOnlineReconciler } from './onlineReconciler'
import { makeContextGuard } from './syncContext'

const SYNC_CONFIG = {
  PULL_INTERVAL_MS: 5 * 60 * 1000, // 5 minutos (incremental)
  PAGE_SIZE: 500,
} as const

// BACKSTOP de seguridad (Fase D): con el change-feed la full_reconciliation es
// event-driven (se dispara cuando la revisión avanza). Este backstop periódico cubre el
// caso raro de gap por orden de commit o de un reconcile parcial fallido. 12 × 5 min =
// ~1 h. Ya NO es el mecanismo principal de frescura (eso es la revisión monotónica).
const SAFETY_FULL_EVERY = 12

// Contrato de sincronización paginada v2 (ESCALA 0B: tres estados + identidad).
// Cambiarla invalida runs/checkpoints previos (fuerza nueva instantánea).
const SYNC_CONTRACT_VERSION = 2

// pageSize de PRUEBA (solo QA/dev): permite forzar paginación multipágina con
// datasets pequeños SIN bajar el pageSize productivo. null = usar el productivo.
// Se fija únicamente vía el hook dev-gated (ver DataSyncManager); en producción
// (vite build, import.meta.env.DEV=false) el hook no existe y esto queda null.
let _testPageSize: number | null = null
export function __setTestPageSize(n: number | null): void {
  _testPageSize = n
}

/** Contexto operativo válido: sin userId no se sincroniza (Bloque 8). */
export interface SyncContext {
  userId: string
  businessId: string | null
  storeId: string
}

let pullIntervalId: ReturnType<typeof setInterval> | null = null

// ── Control del worker ─────────────────────────────────────────────────────

export function startDataSyncWorker(ctx: SyncContext): () => void {
  if (!ctx.userId || !ctx.storeId) return () => {}

  // CANCELACIÓN POR CONTEXTO (Fase C): al cambiar usuario/empresa/tienda o al hacer
  // logout, DataSyncManager llama al cleanup → disposed=true. Toda corrida EN VUELO con
  // el ctx viejo se aborta (shouldContinue=false entre páginas) y NUNCA promueve
  // (guardas isActive), así que jamás se publica catálogo de otro scope.
  let disposed = false
  const isActive = () => !disposed

  // Arranque: el change-feed decide si hace falta full (revisión avanzó desde la última
  // sesión) o basta incremental. forceFull en el primer ciclo garantiza una base fresca.
  if (navigator.onLine) void maybeReconcile(ctx, isActive, true)

  // FASE D: la frescura la gobierna la REVISIÓN monotónica del catálogo (change-feed),
  // no un temporizador ciego. Cada tick: si la revisión del servidor avanzó respecto a
  // la última completada → full_reconciliation (refleja cambios compuestos y PURGA
  // bajas); si no cambió → incremental barato. `SAFETY_FULL_EVERY` es sólo backstop.
  let tick = 0
  pullIntervalId = setInterval(() => {
    if (!navigator.onLine || disposed) return
    tick++
    void maybeReconcile(ctx, isActive, tick % SAFETY_FULL_EVERY === 0)
  }, SYNC_CONFIG.PULL_INTERVAL_MS)

  // Al RECUPERAR conexión se reconcilia según el change-feed (tras estar offline la
  // revisión casi seguro avanzó → full que purga bajas). El reconciliador amortigua el
  // evento `online` (debounce + dedup + cooldown/backoff + jitter) contra tormentas.
  const online = createOnlineReconciler({
    run: () => maybeReconcile(ctx, isActive, false),
    isOnline: () => navigator.onLine,
    isActive,
  })
  const handleOnline = () => online.trigger()
  window.addEventListener('online', handleOnline)

  return () => {
    disposed = true
    if (pullIntervalId) clearInterval(pullIntervalId)
    window.removeEventListener('online', handleOnline)
    online.dispose()
  }
}

// ── Pull de datos frescos ──────────────────────────────────────────────────

export async function pullFreshData(
  ctx: SyncContext,
  mode: SyncRunMode = 'incremental',
  isActive?: () => boolean
): Promise<boolean> {
  if (!ctx.userId || !ctx.storeId) return false
  // 0B.10 P5: sin guard explícito se usa el COMPROBADOR REAL de contexto (usuario/
  // empresa/tienda + generación), nunca un `() => true` permisivo.
  const active = isActive ?? makeContextGuard(ctx)
  if (!active()) return false // contexto ya cambiado antes de arrancar → no-op

  // RESULTADO GLOBAL (0B.9 P1#3): antes esto era Promise.all dentro de un try/catch que
  // se TRAGABA el error y devolvía void → el caller no podía distinguir "todo fresco" de
  // "una entidad falló", y la revisión del change-feed se guardaba igual, dejando datos
  // stale que ya nunca se re-reconciliaban. Ahora se exige que TODAS las entidades
  // completen (allSettled + cada pull devuelve si completó; ran=false tampoco es éxito).
  const results = await Promise.allSettled([
    pullBusinesses(ctx, mode, active),
    pullStores(ctx, mode, active),
    pullProducts(ctx, mode, active),
    pullCategories(ctx, mode, active),
    pullClients(ctx, mode, active),
    // NB: el stock por tienda lo provee pullProducts (get_pos_products_page) por
    // surtido. No se sincroniza inventory_balances crudo (traía productos fuera
    // de surtido y contaminaba la señal "autorizado aquí" de db.stock).
    pullUserBusinessAccess(ctx, mode, active),
    pullUserStoreAccess(ctx, mode, active),
    pullFeatureFlags(active),
  ])

  const ok = results.every(r => r.status === 'fulfilled' && r.value === true)
  if (!ok) {
    const failed = results
      .map((r, i) => (r.status === 'fulfilled' && r.value === true ? null : i))
      .filter(i => i !== null)
    console.warn('[SyncWorker] Pull incompleto — índices no OK:', failed)
  }
  return ok
}

/** Reconciliación completa periódica (Bloque 5): desde cero, snapshot propio. */
export async function reconcileAllData(
  ctx: SyncContext,
  isActive?: () => boolean
): Promise<boolean> {
  return pullFreshData(ctx, 'full_reconciliation', isActive ?? makeContextGuard(ctx))
}

// ── Helper genérico de sincronización paginada por tabla ────────────────────

type ScopeFn = (
  q: ReturnType<ReturnType<typeof supabase.from>['select']>
) => ReturnType<ReturnType<typeof supabase.from>['select']>

async function syncPaginatedTable<TRow extends Record<string, unknown>, TLocal>(args: {
  entity: string
  table: string
  select: string
  idCol?: string
  scope: SyncScope
  mode: SyncRunMode
  applyScope: ScopeFn
  cursorOf: (row: TRow) => KeysetCursor
  map: (row: TRow) => TLocal
  /** Fase C: ¿el contexto sigue vigente? Aborta la corrida y bloquea la promoción. */
  isActive?: () => boolean
  /** Path legacy: persiste cada página por su cuenta (bulkPut idempotente). */
  put?: (rows: TLocal[]) => Promise<void>
  /**
   * Path ATÓMICO + PURGA (Fase A, privacidad local): acumula todas las páginas en
   * memoria y promueve en UNA transacción Dexie solo al completar; en
   * full_reconciliation borra de `table` las filas NO vistas (eliminadas/revocadas/
   * fuera de alcance) → los datos que el servidor ya no autoriza desaparecen local-
   * mente. Es autoritativo por scope: `seen` acumula TODAS las páginas antes de purgar,
   * así que nunca borra filas válidas de un run incompleto.
   */
  atomic?: {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- insert-type any: EntityTable hace la PK opcional; solo usamos bulkPut/filter/delete.
    table: Table<TLocal, string, any>
    keyOf: (l: TLocal) => string
    /** Purga por defecto: en full, borra TODA fila no vista. */
    purge?: boolean
    /**
     * Purga con regla propia (0B.10). Devuelve true si la fila debe BORRARSE. Necesario
     * cuando `seen` no cubre toda la tabla: p.ej. los clientes se descargan por TIENDA
     * pero se consultan por EMPRESA, así que borrar "todo lo no visto" eliminaría los
     * clientes legítimos de otras tiendas de la misma empresa.
     */
    purgeWhere?: (l: TLocal, seen: Set<string>) => boolean
  }
}): Promise<boolean> {
  const idCol = args.idCol ?? 'id'
  const buf: TLocal[] = []
  const seen = new Set<string>()

  // P2#7/P2#4: lock por SCOPE (no por modo) → una sola corrida por entidad+scope a la
  // vez, incluso entre full e incremental, que comparten el mismo registro sync_run.
  const outcome = await withScopeLock(scopeFingerprint(args.scope), async handle => {
    const res = await runPaginatedSync<TRow>({
      scope: args.scope,
      mode: args.mode,
      // CIERRE ATÓMICO TOTAL (0B.9 P1#2): datos+purga+sync_run+checkpoint+freshness en
      // UNA transacción. `promote` corre dentro; si el contexto cambió justo al
      // completar, lanza → la transacción aborta y NADA se publica (scope viejo).
      freshnessEntity: args.entity,
      promoteTables: args.atomic ? [args.atomic.table] : undefined,
      promote: async () => {
        // PROPIEDAD, 4ª comprobación (0B.9 P2#7): DENTRO de la promoción. Si el lease
        // expiró o el contexto cambió, lanzar aborta la transacción → no se publica nada.
        if (!(await handle.isOwner())) throw new Error('LOCK_PERDIDO')
        if (!(args.isActive?.() ?? true)) throw new Error('CONTEXTO_CAMBIADO')
        if (!args.atomic) return
        const at = args.atomic
        await at.table.bulkPut(buf)
        // PRIVACIDAD (Fase A): en full, purga autoritativa por scope. `seen` es completo.
        if (args.mode === 'full_reconciliation') {
          if (at.purgeWhere) {
            const where = at.purgeWhere
            await at.table.filter(l => where(l, seen)).delete()
          } else if (at.purge) {
            await at.table.filter(l => !seen.has(at.keyOf(l))).delete()
          }
        }
      },
      // Persistencia atómica ⇒ nunca reanudar desde un cursor intermedio (perdería
      // páginas no persistidas y purgaría con `seen` incompleto). Siempre desde origen.
      noResume: !!args.atomic,
      // Fase B+C: aborta si esta pestaña deja de ser dueña del lock (isOwner) o si el
      // contexto operativo cambió (isActive) a media corrida.
      shouldContinue: async () => (await handle.isOwner()) && (args.isActive?.() ?? true),
      pageSize: _testPageSize ?? SYNC_CONFIG.PAGE_SIZE,
      serverBoundary: async () => {
        let q = args.applyScope(supabase.from(args.table).select('updated_at')) as ReturnType<
          ReturnType<typeof supabase.from>['select']
        >
        q = q.order('updated_at', { ascending: false }).limit(1) as typeof q
        const { data, error } = (await q) as {
          data: Array<{ updated_at: string }> | null
          error: PostgrestError | null
        }
        if (error) return { error }
        return { boundary: data?.[0]?.updated_at ?? null }
      },
      fetchPage: async ({ boundary, cursor, pageSize }) => {
        let q = args.applyScope(supabase.from(args.table).select(args.select)) as ReturnType<
          ReturnType<typeof supabase.from>['select']
        >
        q = q.lte('updated_at', boundary) as typeof q
        const or = keysetOrFilter(cursor, idCol)
        if (or) q = q.or(or) as typeof q
        q = q
          .order('updated_at', { ascending: true })
          .order(idCol, { ascending: true })
          .limit(pageSize) as typeof q
        const { data, error } = (await q) as { data: TRow[] | null; error: PostgrestError | null }
        if (error) return { error }
        return { rows: data ?? [] }
      },
      cursorOf: args.cursorOf,
      persistPage: async rows => {
        const mapped = rows.map(args.map)
        if (args.atomic) {
          // Acumula en el staging; NO escribe Dexie (promoción atómica al completar).
          for (const l of mapped) {
            buf.push(l)
            seen.add(args.atomic.keyOf(l))
          }
        } else {
          await args.put!(mapped)
        }
        return rows.length
      },
    })

    // RESULTADO GLOBAL (0B.9 P1#3): la entidad sólo cuenta como OK si completó.
    return res.completed
  })
  // ran=false (otra pestaña tenía el lock) NO es éxito: no se pudo verificar frescura.
  return outcome.ran ? outcome.value : false
}

/** Construye el scope de una entidad para un contexto operativo. */
function scopeFor(
  entity: string,
  ctx: SyncContext,
  opts: { business?: boolean; store?: boolean } = {}
): SyncScope {
  return {
    entity,
    userId: ctx.userId,
    businessId: opts.business ? ctx.businessId : null,
    storeId: opts.store ? ctx.storeId : null,
    schemaVersion: SYNC_CONTRACT_VERSION,
  }
}

// ── Productos (scope usuario+empresa+TIENDA; vía RPC paginada por surtido) ────
// P1#3: el catálogo operativo proviene de get_pos_products_page, que YA aplica
// empresa + tienda + surtido (store_product_assortment) + visibilidad + stock. No
// se descarga la tabla `products` cruda por empresa (eso ignoraba el surtido y
// dejaba a matchBiz como defensa final). La RPC valida auth.uid()+alcance.

type PosPageRow = {
  product_id: string
  name: string
  code: string | null
  barcode: string | null
  brand: string | null
  category_id: string | null
  category_name: string | null
  category_emoji: string | null
  price_retail: number
  price_b: number | null
  price_c: number | null
  // P0 (00221): la proyección de sync ya NO trae cost. Se conserva la ausencia como
  // invariante (el costo sólo se lee, autorizado, vía RPC get_products_cost).
  tax_rate: number
  tax_included: boolean
  emoji: string | null
  photo_url: string | null
  featured: boolean
  track_stock: boolean
  active: boolean
  stock: number
  reserved: number
  business_id: string | null
  updated_at: string
  snapshot_boundary: string
}

async function pullProducts(
  ctx: SyncContext,
  mode: SyncRunMode,
  isActive?: () => boolean
): Promise<boolean> {
  const active = isActive ?? makeContextGuard(ctx)
  // Sin empresa activa no hay catálogo que sincronizar: NO es éxito (fail-safe → no se
  // persiste revisión). getServerCatalogRevision también devuelve null en este caso.
  if (!ctx.businessId) return false
  const businessId = ctx.businessId
  const storeId = ctx.storeId
  const scope = scopeFor('products', ctx, { business: true, store: true })

  // STAGING en memoria: las páginas se ACUMULAN aquí y se promueven de forma ATÓMICA
  // solo al completar. Nada se escribe en Dexie hasta el final → un run interrumpido
  // NO publica catálogo parcial y `seen` (autorizados) queda COMPLETO antes de purgar.
  const productsBuf: LocalProduct[] = []
  const stockBuf: LocalStock[] = []
  const seen = new Set<string>()

  // P2#7/P2#4: lock por SCOPE (no por modo) → full e incremental de productos nunca
  // corren a la vez ni pisan el mismo sync_run.
  const outcome = await withScopeLock(scopeFingerprint(scope), async handle => {
    const res = await runPaginatedSync<PosPageRow>({
      scope,
      mode,
      // Persistencia atómica ⇒ nunca reanudar desde un cursor intermedio (perdería
      // páginas no persistidas y purgaría con `seen` incompleto). Siempre desde origen.
      noResume: true,
      // Fase B+C: aborta si esta pestaña deja de ser dueña del lock (isOwner) o si el
      // contexto operativo cambió (isActive) a media corrida.
      shouldContinue: async () => (await handle.isOwner()) && active(),
      pageSize: _testPageSize ?? 1000,
      serverBoundary: async () => {
        const { data, error } = await supabase.rpc('get_pos_products_page', {
          p_business_id: businessId,
          p_store_id: storeId,
          p_after_updated_at: null,
          p_after_id: null,
          p_snapshot_boundary: null,
          p_page_size: 1,
        })
        if (error) return { error }
        return { boundary: (data?.[0] as PosPageRow | undefined)?.snapshot_boundary ?? null }
      },
      fetchPage: async ({ boundary, cursor, pageSize }) => {
        const { data, error } = await supabase.rpc('get_pos_products_page', {
          p_business_id: businessId,
          p_store_id: storeId,
          p_after_updated_at: cursor?.updatedAt ?? null,
          p_after_id: cursor?.id ?? null,
          p_snapshot_boundary: boundary,
          p_page_size: pageSize,
        })
        if (error) return { error }
        return { rows: (data ?? []) as PosPageRow[] }
      },
      cursorOf: r => ({ updatedAt: r.updated_at, id: r.product_id }),
      // Acumula en el staging; NO escribe Dexie (promoción atómica al completar).
      persistPage: async rows => {
        const now = Date.now()
        for (const p of rows) {
          productsBuf.push({
            id: p.product_id,
            name: p.name,
            code: p.code,
            barcode: p.barcode,
            brand: p.brand,
            category_id: p.category_id,
            category_name: p.category_name,
            category_emoji: p.category_emoji,
            price_retail: p.price_retail,
            price_b: p.price_b ?? null,
            price_c: p.price_c ?? null,
            tax_rate: p.tax_rate,
            tax_included: p.tax_included,
            emoji: p.emoji,
            photo_url: p.photo_url,
            featured: p.featured,
            track_stock: p.track_stock,
            active: p.active,
            business_id: p.business_id ?? null,
            synced_at: now,
          })
          stockBuf.push({
            id: `${p.product_id}_${storeId}`,
            product_id: p.product_id,
            store_id: storeId,
            stock: Number(p.stock),
            reserved: Number(p.reserved),
            available: Math.max(0, Number(p.stock) - Number(p.reserved)),
            updated_at: p.updated_at,
            synced_at: now,
          })
          seen.add(p.product_id)
        }
        return rows.length
      },
      // CIERRE ATÓMICO TOTAL (0B.9 P1#2): productos + stock + purga + sync_run +
      // checkpoint + freshness en UNA sola transacción. Antes el sync_run/checkpoint y
      // sync_meta se escribían FUERA de la transacción de datos.
      freshnessEntity: 'products',
      promoteTables: [db.products, db.stock],
      promote: async () => {
        // PROPIEDAD, 4ª comprobación (0B.9 P2#7): dentro de la promoción. Fase C: si el
        // contexto cambió o el lease expiró justo al completar, abortar la transacción →
        // no se publica el catálogo de un scope viejo ni de una pestaña sin propiedad.
        if (!(await handle.isOwner())) throw new Error('LOCK_PERDIDO')
        if (!active()) throw new Error('CONTEXTO_CAMBIADO')
        await db.products.bulkPut(productsBuf)
        await db.stock.bulkPut(stockBuf)
        if (mode === 'full_reconciliation') {
          await db.stock
            .where('store_id')
            .equals(storeId)
            .and(s => !seen.has(s.product_id))
            .delete()
          // PRIVACIDAD (Fase A #2): tras purgar el stock no autorizado de esta tienda,
          // los productos que ya no tienen stock en NINGUNA tienda (perdieron acceso en
          // todo el scope local) se eliminan de db.products → no retienen nombre/costo/
          // PII en Dexie. Un producto aún surtido en otra tienda conserva su stock ahí,
          // así que NO se borra. db.stock es la autoridad de "autorizado aquí".
          const authorized = new Set<string>()
          await db.stock.each(s => {
            authorized.add(s.product_id)
          })
          await db.products.filter(p => !authorized.has(p.id)).delete()
        }
      },
    })

    // RESULTADO GLOBAL (0B.9 P1#3): la entidad sólo cuenta como OK si completó.
    return res.completed
  })
  // ran=false (otra pestaña tenía el lock) NO es éxito.
  return outcome.ran ? outcome.value : false
}

// ── Categorías (scope por usuario + empresa) ─────────────────────────────────

type CategoryRow = {
  id: string
  name: string
  parent_id: string | null
  emoji: string | null
  sort_order: number
  active: boolean
  business_id: string | null
  updated_at: string
}

async function pullCategories(
  ctx: SyncContext,
  mode: SyncRunMode,
  isActive?: () => boolean
): Promise<boolean> {
  return syncPaginatedTable<CategoryRow, LocalCategory>({
    entity: 'categories',
    table: 'categories',
    select: 'id, name, parent_id, emoji, sort_order, active, business_id, updated_at',
    scope: scopeFor('categories', ctx, { business: true }),
    mode,
    isActive,
    applyScope: q => q.eq('active', true),
    cursorOf: r => ({ updatedAt: r.updated_at, id: r.id }),
    map: c => ({
      id: c.id,
      name: c.name,
      parent_id: c.parent_id,
      emoji: c.emoji,
      sort_order: c.sort_order,
      active: c.active,
      business_id: c.business_id ?? null,
      synced_at: Date.now(),
    }),
    // 0B.10 P3/P4: antes usaba el path legacy `put`, que publicaba PÁGINA POR PÁGINA y
    // no purgaba nada → una categoría desactivada/eliminada sobrevivía en Dexie para
    // siempre (visible offline), y un fallo a mitad dejaba el catálogo de categorías
    // mezclado. Ahora: staging + `seen` + promoción atómica + PURGA AUTORITATIVA POR
    // business_id (la consulta filtra active=true, así que lo inactivo no entra en
    // `seen` y se borra). No se tocan las categorías de otras empresas ni las globales.
    atomic: {
      table: db.categories,
      keyOf: c => c.id,
      purgeWhere: (c, seen) => c.business_id === ctx.businessId && !seen.has(c.id),
    },
  })
}

// ── Clientes (scope por usuario + empresa + tienda) ──────────────────────────

type ClientRow = {
  id: string
  name: string
  phone_e164: string
  email: string | null
  level: string | null
  total_purchases: number
  total_visits: number
  last_visit: string | null
  active: boolean
  store_id: string // tienda de ORIGEN (servidor)
  updated_at: string
}

async function pullClients(
  ctx: SyncContext,
  mode: SyncRunMode,
  isActive?: () => boolean
): Promise<boolean> {
  return syncPaginatedTable<ClientRow, LocalClient>({
    entity: 'clients',
    table: 'clients',
    // `store_id` viene del SERVIDOR: es la tienda de ORIGEN del cliente (donde se dio
    // de alta, donde vive su historial y donde aplica su unicidad de teléfono).
    select:
      'id, name, phone_e164, email, level, total_purchases, total_visits, last_visit, active, store_id, updated_at',
    scope: scopeFor('clients', ctx, { business: true, store: true }),
    mode,
    isActive,
    applyScope: q => q.eq('store_id', ctx.storeId).is('deleted_at', null),
    cursorOf: r => ({ updatedAt: r.updated_at, id: r.id }),
    map: c => ({
      id: c.id,
      name: c.name,
      phone_e164: c.phone_e164,
      email: c.email,
      level: (['pro', 'vip'].includes(String(c.level).toLowerCase())
        ? String(c.level).toLowerCase()
        : 'final') as 'final' | 'pro' | 'vip',
      total_purchases: c.total_purchases,
      total_visits: c.total_visits,
      last_visit: c.last_visit ?? null,
      active: c.active,
      synced_at: Date.now(),
      // ALCANCE (0B.9 P1#5 → 0B.10 P2): empresa activa + TIENDA DE ORIGEN del servidor
      // (no ctx.storeId). Así el cliente conserva su procedencia aunque se consulte
      // desde otra tienda de la misma empresa.
      business_id: ctx.businessId,
      store_id: c.store_id,
    }),
    atomic: {
      table: db.clients,
      keyOf: c => c.id,
      // PURGA PRECISA (0B.10 P2). `seen` sólo cubre los clientes de la TIENDA que se
      // acaba de descargar, pero db.clients guarda los de toda la EMPRESA (consulta
      // corporativa). Borrar "todo lo no visto" eliminaría clientes legítimos de otras
      // tiendas. Se borra exactamente:
      //   (a) todo cliente de OTRA empresa → PII no autorizada fuera de Dexie; y
      //   (b) los de ESTA tienda de origen que el servidor ya no devuelve
      //       (eliminados/desactivados/revocados).
      // Los de otras tiendas de la misma empresa se conservan intactos.
      purgeWhere: (c, seen) =>
        c.business_id !== ctx.businessId || (c.store_id === ctx.storeId && !seen.has(c.id)),
    },
  })
}

// ── Empresas (scope por usuario) ─────────────────────────────────────────────

type BusinessRow = {
  id: string
  brand: string
  name: string
  legal_name: string | null
  rfc: string | null
  status: string
  license: Record<string, unknown> | null
  config: Record<string, unknown> | null
  created_at: string
  updated_at: string
}

async function pullBusinesses(
  ctx: SyncContext,
  mode: SyncRunMode,
  isActive?: () => boolean
): Promise<boolean> {
  return syncPaginatedTable<BusinessRow, LocalBusiness>({
    entity: 'businesses',
    table: 'businesses',
    select: 'id, brand, name, legal_name, rfc, status, license, config, created_at, updated_at',
    scope: scopeFor('businesses', ctx),
    mode,
    isActive,
    applyScope: q => q,
    cursorOf: r => ({ updatedAt: r.updated_at, id: r.id }),
    map: b => ({
      id: b.id,
      brand: b.brand,
      name: b.name,
      legal_name: b.legal_name ?? null,
      rfc: b.rfc ?? null,
      status: b.status,
      license: b.license ?? {},
      config: b.config ?? {},
      created_at: b.created_at,
      updated_at: b.updated_at,
      synced_at: Date.now(),
    }),
    // 0B.10 P4: staging + promoción atómica (ya no página por página) + purga
    // autoritativa: lo que el servidor deja de autorizar (accesos revocados, tiendas
    // o empresas retiradas) desaparece de Dexie en la reconciliación completa.
    atomic: { table: db.businesses, keyOf: x => x.id, purge: true },
  })
}

// ── Tiendas (scope por usuario) ──────────────────────────────────────────────

type StoreRow = {
  id: string
  code: string
  brand: string
  business_id: string | null
  name: string
  city: string
  state: string
  address: string | null
  phone: string | null
  wa_number: string | null
  timezone: string
  active: boolean
  config: Record<string, unknown> | null
  updated_at: string
}

async function pullStores(
  ctx: SyncContext,
  mode: SyncRunMode,
  isActive?: () => boolean
): Promise<boolean> {
  return syncPaginatedTable<StoreRow, LocalStore>({
    entity: 'stores',
    table: 'stores',
    select:
      'id, code, brand, business_id, name, city, state, address, phone, wa_number, timezone, active, config, updated_at',
    scope: scopeFor('stores', ctx),
    mode,
    isActive,
    applyScope: q => q.is('deleted_at', null),
    cursorOf: r => ({ updatedAt: r.updated_at, id: r.id }),
    map: s => ({
      id: s.id,
      code: s.code,
      brand: s.brand,
      business_id: s.business_id ?? null,
      name: s.name,
      city: s.city,
      state: s.state,
      address: s.address ?? null,
      phone: s.phone ?? null,
      wa_number: s.wa_number ?? null,
      timezone: s.timezone,
      active: s.active,
      config: s.config ?? {},
      synced_at: Date.now(),
    }),
    // 0B.10 P4: staging + promoción atómica (ya no página por página) + purga
    // autoritativa: lo que el servidor deja de autorizar (accesos revocados, tiendas
    // o empresas retiradas) desaparece de Dexie en la reconciliación completa.
    atomic: { table: db.stores, keyOf: x => x.id, purge: true },
  })
}

// ── Acceso corporativo de usuario (scope por usuario; RLS filtra) ────────────

type UserBusinessAccessRow = {
  id: string
  user_id: string
  business_id: string
  role: string
  permissions: string[] | null
  is_primary: boolean
  active: boolean
  updated_at: string
}

async function pullUserBusinessAccess(
  ctx: SyncContext,
  mode: SyncRunMode,
  isActive?: () => boolean
): Promise<boolean> {
  return syncPaginatedTable<UserBusinessAccessRow, LocalUserBusinessAccess>({
    entity: 'user_business_access',
    table: 'user_business_access',
    select: 'id, user_id, business_id, role, permissions, is_primary, active, updated_at',
    scope: scopeFor('user_business_access', ctx),
    mode,
    isActive,
    applyScope: q => q.eq('active', true),
    cursorOf: r => ({ updatedAt: r.updated_at, id: r.id }),
    map: r => ({
      id: r.id,
      user_id: r.user_id,
      business_id: r.business_id,
      role: r.role,
      permissions: r.permissions ?? [],
      is_primary: r.is_primary,
      active: r.active,
      updated_at: r.updated_at,
      synced_at: Date.now(),
    }),
    // 0B.10 P4: staging + promoción atómica (ya no página por página) + purga
    // autoritativa: lo que el servidor deja de autorizar (accesos revocados, tiendas
    // o empresas retiradas) desaparece de Dexie en la reconciliación completa.
    atomic: { table: db.userBusinessAccess, keyOf: x => x.id, purge: true },
  })
}

type UserStoreAccessRow = {
  id: string
  user_id: string
  store_id: string
  business_id: string | null
  role: string | null
  permissions: string[] | null
  is_primary: boolean
  active: boolean
  updated_at: string
}

async function pullUserStoreAccess(
  ctx: SyncContext,
  mode: SyncRunMode,
  isActive?: () => boolean
): Promise<boolean> {
  return syncPaginatedTable<UserStoreAccessRow, LocalUserStoreAccess>({
    entity: 'user_store_access',
    table: 'user_store_access',
    select: 'id, user_id, store_id, business_id, role, permissions, is_primary, active, updated_at',
    scope: scopeFor('user_store_access', ctx),
    mode,
    isActive,
    applyScope: q => q.eq('active', true),
    cursorOf: r => ({ updatedAt: r.updated_at, id: r.id }),
    map: r => ({
      id: r.id,
      user_id: r.user_id,
      store_id: r.store_id,
      business_id: r.business_id ?? null,
      role: r.role ?? null,
      permissions: r.permissions ?? [],
      is_primary: r.is_primary,
      active: r.active,
      updated_at: r.updated_at,
      synced_at: Date.now(),
    }),
    // 0B.10 P4: staging + promoción atómica (ya no página por página) + purga
    // autoritativa: lo que el servidor deja de autorizar (accesos revocados, tiendas
    // o empresas retiradas) desaparece de Dexie en la reconciliación completa.
    atomic: { table: db.userStoreAccess, keyOf: x => x.id, purge: true },
  })
}

// ── Feature flags (tabla pequeña, no requiere paginación) ────────────────────

/**
 * Feature flags: SNAPSHOT AUTORITATIVO con cancelación por contexto (0B.12 O3/O4).
 *
 * O3 — antes, una respuesta VACÍA devolvía éxito y salía sin hacer nada: los flags
 * locales de un snapshot anterior sobrevivían indefinidamente (un flag retirado en el
 * servidor seguía activo offline) y la frescura no avanzaba. Una respuesta vacía es un
 * snapshot válido: significa "no hay flags", así que debe PURGAR y marcar frescura.
 *
 * O4 — no recibía guard de contexto: un fetch lento que volvía tras un logout o un
 * cambio de tienda persistía igualmente y contaba como éxito, permitiendo además que la
 * revisión global avanzara. Ahora se comprueba antes de la red, después de la red y
 * DENTRO de la transacción de persistencia (donde lanzar aborta y no publica nada).
 */
export async function pullFeatureFlags(isActive: () => boolean): Promise<boolean> {
  if (!isActive()) return false // O4: antes de la red

  const { data, error } = await supabase
    .from('feature_flags')
    .select('key, enabled, enabled_for_stores, enabled_for_roles')

  if (!isActive()) return false // O4: después de la red, antes de persistir
  if (error) return false // no OK → bloquea la revisión global

  // O3: `[]` es un snapshot autoritativo, no un "sin datos" que se ignora.
  const rows = (data ?? []).map(f => ({
    key: f.key,
    enabled: f.enabled,
    enabled_for_stores: (f.enabled_for_stores as string[]) ?? [],
    enabled_for_roles: (f.enabled_for_roles as string[]) ?? [],
    synced_at: Date.now(),
  }))
  const seen = new Set(rows.map(r => r.key))

  try {
    // Cierre atómico: datos + purga + frescura en UNA transacción.
    await db.transaction('rw', [db.feature_flags, db.sync_meta], async () => {
      if (!isActive()) throw new Error('CONTEXTO_CAMBIADO') // O4: dentro de la persistencia
      if (rows.length > 0) await db.feature_flags.bulkPut(rows)
      // Purga autoritativa: lo que el servidor ya no devuelve desaparece de Dexie.
      await db.feature_flags.filter(f => !seen.has(f.key)).delete()
      await db.sync_meta.put({ table_name: 'feature_flags', last_sync_at: Date.now(), etag: null })
    })
    return true
  } catch {
    return false // no se reconoce éxito → la revisión global no avanza
  }
}

// ── Change-feed del catálogo (ESCALA 0B Fase D) ──────────────────────────────
// La revisión monotónica (get_pos_catalog_revision, migr 00184) es la AUTORIDAD de
// "algo cambió" en products/inventory_balances/store_product_assortment/
// product_business_access — incluidas BAJAS (retiro de surtido, acceso revocado,
// deleted/inactive) que ningún cursor por updated_at puede ver. NO se usa
// products.updated_at como señal de cambio: el paginador lo usa sólo como clave de
// orden DENTRO de una instantánea.

function catalogRevKey(ctx: SyncContext): string {
  return `catalog_rev:${ctx.businessId ?? '_'}:${ctx.storeId}`
}

/** Revisión del catálogo en el servidor para el scope; null si offline/no autorizado. */
export async function getServerCatalogRevision(ctx: SyncContext): Promise<number | null> {
  if (!ctx.businessId) return null
  const { data, error } = await supabase.rpc('get_pos_catalog_revision', {
    p_business_id: ctx.businessId,
    p_store_id: ctx.storeId,
  })
  if (error || data === null || data === undefined) return null
  const n = Number(data)
  return Number.isFinite(n) ? n : null
}

/** Última revisión con la que se completó una reconciliación (persistida por scope). */
async function getStoredCatalogRevision(ctx: SyncContext): Promise<number> {
  const row = await db.app_meta.get(catalogRevKey(ctx)).catch(() => undefined)
  const n = row ? Number(row.value) : 0
  return Number.isFinite(n) ? n : 0
}

async function setStoredCatalogRevision(ctx: SyncContext, rev: number): Promise<void> {
  await db.app_meta.put({ key: catalogRevKey(ctx), value: String(rev), updated_at: Date.now() })
}

/**
 * Decide QUÉ sincronizar según el change-feed (Fase D):
 *   • revisión avanzó respecto a la última completada → full_reconciliation (refleja
 *     cambios compuestos y PURGA bajas). La revisión servidor se captura ANTES de
 *     reconciliar y se persiste sólo si el contexto sigue vigente al terminar: un cambio
 *     ocurrido DURANTE el sync deja la revisión servidor por delante → el próximo tick
 *     vuelve a reconciliar (convergencia; cubre la instantánea entre páginas).
 *   • sin cambios → incremental barato (delta de products.updated_at).
 *   • backstop de seguridad periódico (gap por orden de commit / reconcile parcial):
 *     full_reconciliation cada SAFETY_FULL_EVERY intervalos.
 */
export async function maybeReconcile(
  ctx: SyncContext,
  isActive: (() => boolean) | undefined,
  forceFull: boolean
): Promise<void> {
  // 0B.10 P5: guard REAL por contexto+generación cuando el caller no aporta uno.
  const active = isActive ?? makeContextGuard(ctx)
  const serverRev = await getServerCatalogRevision(ctx)
  const lastRev = await getStoredCatalogRevision(ctx)
  if ((serverRev !== null && serverRev > lastRev) || forceFull) {
    const ok = await pullFreshData(ctx, 'full_reconciliation', active)
    // RESULTADO GLOBAL (0B.9 P1#3): la revisión SÓLO se persiste si TODAS las entidades
    // completaron. Si una falló (o no corrió por lock ajeno), NO se guarda → la revisión
    // servidor sigue por delante y el próximo tick vuelve a reconciliar. Guardar aquí sin
    // éxito global dejaría datos stale que ya nunca se re-reconcilian (fail-safe).
    if (ok && active() && serverRev !== null) await setStoredCatalogRevision(ctx, serverRev)
  } else {
    await pullFreshData(ctx, 'incremental', active)
  }
}
